/**
 * Keeps an open tab from outliving the deploy it was loaded from.
 *
 * A single-page app is the one thing a deploy cannot restart: whoever had the
 * site open keeps running yesterday's bundle against today's API until they
 * happen to reload. So the tab asks the server which build it is serving, and
 * when that answer changes it throws away everything the browser has cached for
 * this origin and reloads onto the new one.
 *
 * The server computes the version by hashing the built `index.html`, which
 * names every hashed asset — see `apps/server/src/version.ts`.
 */

import { useEffect, useRef } from 'react';
import type { ClientVersion } from '@gl/shared';

const CHECK_INTERVAL_MS = 60_000;

/**
 * The version a reload has already been performed for. Survives the reload
 * itself (that is the point) but not the tab, so it cannot wedge a session:
 * without it, a browser that ignored the reload would loop on it forever.
 */
const RELOADED_FOR_KEY = 'gl.reloaded-for-version';

async function fetchVersion(): Promise<string | null> {
  try {
    const response = await fetch('/api/version', { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) return null;
    const body = (await response.json()) as ClientVersion;
    return typeof body.version === 'string' ? body.version : null;
  } catch {
    // Offline, or the server is mid-restart. Nothing to do but ask again later.
    return null;
  }
}

/** Everything the browser is holding for this origin, short of the reload. */
async function dropCaches(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // A cleared cache is a nicety; the reload is what actually matters.
  }
}

/** True while someone is typing, when a reload would throw their work away. */
function isEditing(): boolean {
  const element = document.activeElement;
  if (!(element instanceof HTMLElement)) return false;
  return (
    element.isContentEditable ||
    element.tagName === 'INPUT' ||
    element.tagName === 'TEXTAREA' ||
    element.tagName === 'SELECT'
  );
}

function readReloadedFor(): string | null {
  try {
    return sessionStorage.getItem(RELOADED_FOR_KEY);
  } catch {
    return null;
  }
}

function writeReloadedFor(version: string): void {
  try {
    sessionStorage.setItem(RELOADED_FOR_KEY, version);
  } catch {
    // Private browsing can refuse to store. Worst case a reload repeats once.
  }
}

/**
 * Watches for a new build and reloads onto it.
 *
 * Mounted once, at the root. Checks on mount, on an interval, and whenever the
 * tab comes back to the foreground — which is when someone who left the site
 * open overnight is most likely to be on an old build.
 */
export function useAutoReloadOnUpdate(): void {
  const loaded = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const version = await fetchVersion();
      if (cancelled || version === null) return;

      // The first answer is simply which build this tab is running.
      if (loaded.current === null) {
        loaded.current = version;
        return;
      }
      if (loaded.current === version) return;

      // Already reloaded once for this build: it did not take, so stop.
      if (readReloadedFor() === version) {
        loaded.current = version;
        return;
      }

      // Wait for a pause rather than wiping a half-written post; the next
      // check comes round soon enough.
      if (isEditing()) return;

      writeReloadedFor(version);
      await dropCaches();
      if (!cancelled) window.location.reload();
    };

    void check();
    const timer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);

    const onForeground = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onForeground);
    window.addEventListener('focus', onForeground);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onForeground);
      window.removeEventListener('focus', onForeground);
    };
  }, []);
}
